<template>
  <v-row :class="!device.isMobile && 'mx-10 mt-2'">
    <category-menu-group-sidebar
      v-model="selectedShiftIndex"
      :selected-menu="selectedMenu"
      :selected-date="selectedDate"
      :selected-shift-key="selectedShiftKey"
      :selected-category="selectedCategory"
      :active-shift-key="activeShiftKey"
      @change-day="changeDay"
      @select-category="selectedCategory = $event"
    />

    <category-menu-group-items-list
      :selected-menu="selectedMenu"
      :selected-date="selectedDate"
      :selected-category="selectedCategory"
      :selected-shift-key="selectedShiftKey"
      :selected-categories="selectedCategories"
      :check-disabled="checkDisabled"
      :has-existing-order="hasExistingOrder"
      @change-day="changeDay"
      @sub-food="decrementFoodQuantity"
      @add-food="addFoodSelection"
      @submit="submitOrder(hasExistingOrder ? 'update' : 'create')"
    />
    <category-menu-group-summary
      v-model="selectedShiftIndex"
      :selected-menu="selectedMenu"
      :active-shift-key="activeShiftKey"
      :selected-shift-key="selectedShiftKey"
      :selected-categories="selectedCategories"
      :check-disabled="checkDisabled"
      :has-existing-order="hasExistingOrder"
      @remove-food="removeFoodSelection"
      @submit="submitOrder(hasExistingOrder ? 'update' : 'create')"
    />

    <category-menu-group-success-overlay v-model="isSuccessDialogVisible" />
  </v-row>
</template>

<script>
import moment from "moment";
import CategoryMenuGroupSidebar from "@/components/categoryMenuGroup/CategoryMenuGroupSidebar.vue";
import CategoryMenuGroupItemsList from "@/components/categoryMenuGroup/CategoryMenuGroupItemsList.vue";
import CategoryMenuGroupSummary from "@/components/categoryMenuGroup/CategoryMenuGroupSummary.vue";
import CategoryMenuGroupSuccessOverlay from "@/components/categoryMenuGroup/CategoryMenuGroupSuccessOverlay.vue";
import menuService from "@/services/menu";
import restaurantService from "@/services/restaurant";
import orderService from "@/services/order";
import hasSnackbar from "@/mixins/hasSnackbar";

export default {
  name: "CategoryMenuGroupOrders",
  mixins: [hasSnackbar],
  components: {
    CategoryMenuGroupSidebar,
    CategoryMenuGroupItemsList,
    CategoryMenuGroupSummary,
    CategoryMenuGroupSuccessOverlay,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedDate: new Date().toISOString().substr(0, 10),
      menus: [],
      selectedCategories: [],
      restaurantOrderTime: null,
      existingOrder: null,
      selectedCategory: null,
      selectedShiftIndex: null,
      isSuccessDialogVisible: false,
      activeShiftKey: "all",
      hasExistingOrder: false,
    };
  },
  created() {
    this.getMenus(true);

    restaurantService
      .getRestaurantNewTimes(this.company.restaurant_id)
      .then((res) => {
        this.restaurantOrderTime = res.data.data;
      })
      .catch((error) => {
        this.showErrorSnackbar(error, this.$t("error_loading_restaurant_times"));
      });
  },
  methods: {
    findSelectionBucketByCategoryAndShift(category, shiftKey) {
      return this.selectedCategories.find(
        (item) => item.category === category && item.shift === shiftKey
      );
    },
    findSelectionBucketByFoodCategoryName(categoryName) {
      return this.selectedCategories.find((item) => item.category === categoryName);
    },
    findMenuFood(shiftKey, category, foodId) {
      const shiftMenu = this.menus[0]?.[shiftKey]?.[category];
      if (!shiftMenu) return null;
      return shiftMenu.find((menuFood) => menuFood.id === foodId) || null;
    },
    buildOrderPayload() {
      const categories = this.selectedCategories.filter((item) => item.foods.length);

      return {
        categories,
        shift: this.selectedShiftIndex + 1,
        restaurant_id: this.company.restaurant_id,
        company_id: this.company.pivot ? this.company.pivot.company_id : 0,
        date: this.selectedDate,
        app_type: 1,
      };
    },
    submitOrder(mode = "create") {
      const data = this.buildOrderPayload();
      const isUpdate = mode === "update";

      if (isUpdate && !this.existingOrder?.id) {
        this.showErrorSnackbar(this.$t("error_updating_order"));
        return;
      }

      const request = isUpdate
        ? orderService.updateGroupFoodOrder(this.existingOrder.id, data)
        : orderService.storeGroupFoodOrder(data);

      request
        .then((res) => {
          if (isUpdate) {
            if (!(data.categories || []).length) this.hasExistingOrder = false;
            this.showSuccessSnackbar(this.$t("order_updated_successfully"));
          } else {
            this.existingOrder = res.data;
            this.hasExistingOrder = true;
            this.showSuccessSnackbar(this.$t("order_created_successfully"));
          }

          this.isSuccessDialogVisible = true;
        })
        .catch((error) => {
          this.showErrorSnackbar(
            error,
            this.$t(isUpdate ? "error_updating_order" : "error_creating_order")
          );
        });
    },
    changeDay(days) {
      this.hasExistingOrder = false;

      this.selectedDate = moment(this.selectedDate)
        .add(days, "days")
        .format("YYYY-MM-DD");

      this.getMenus();
    },
    addFoodSelection(food) {
      const selectionBucket = this.findSelectionBucketByCategoryAndShift(
        this.selectedCategory,
        this.selectedShiftKey
      );
      if (!selectionBucket) return;

      this.activeShiftKey = this.selectedShiftKey;

      const foodIndex = selectionBucket.foods.findIndex(
        (selectedFood) => selectedFood.id === food.id
      );
      if (foodIndex > -1) {
        food.quantity++;
        this.$set(selectionBucket.foods, foodIndex, food);
      } else {
        food.quantity = 1;
        if (this.isCompanyUser) selectionBucket.foods.push(food);
        else selectionBucket.foods = [food];
      }
    },
    decrementFoodQuantity(food) {
      if (food.quantity === 0) return;
      food.quantity--;
      if (food.quantity === 0) {
        const selectionBucket = this.findSelectionBucketByFoodCategoryName(
          food.category.name
        );
        if (!selectionBucket) return;

        const index = selectionBucket.foods.findIndex(
          (selectedFood) => selectedFood.id === food.id
        );
        if (index > -1) selectionBucket.foods.splice(index, 1);
      }
    },
    removeFoodSelection(food, shiftGroup, index) {
      shiftGroup.foods.splice(index, 1);

      this.activeShiftKey = this.selectedCategories.find(
        (item) => item.shift === this.selectedShiftKey && item.foods.length
      )
        ? this.selectedShiftKey
        : "all";

      const foodFromMenu = this.findMenuFood(
        shiftGroup.shift,
        shiftGroup.category,
        food.id
      );
      if (!foodFromMenu) return;
      foodFromMenu.quantity = 0;
    },
    async getMenus(firstLoad = false) {
      if (firstLoad) {
        try {
          let { data } = await restaurantService.checkRestaurantDisabled(
            this.company.restaurant_id
          );
          this.selectedDate = moment(data).format("YYYY-MM-DD");
        } catch (error) {
          this.showErrorSnackbar(error, this.$t("error_loading_restaurant"));
        }
      }

      const date = this.selectedDate;

      this.menus = [];
      this.existingOrder = null;
      this.hasExistingOrder = false;

      try {
        const { data: menusData } = await menuService.getWebAppMenu({
          from: date,
          to: date,
          type: "rucak",
        });
        this.menus = menusData;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_menu"));
        return;
      }

      try {
        const { data: ordersData } = await orderService.getOrdersByDate(date);
        if (ordersData?.length) {
          this.existingOrder = ordersData[0];
          this.applyExistingOrder(this.existingOrder);
        }
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_orders"));
      }
    },
    initializeSelectionBuckets(data) {
      this.selectedCategories = [];
      if (!data) return;

      this.availableShiftKeys.forEach((shiftKey) => {
        const categories = Object.keys(data[shiftKey] || {});
        categories.forEach((categoryKey) => {
          this.selectedCategories.push({
            foods: [],
            category: categoryKey,
            shift: shiftKey,
          });
        });
      });
    },
    applyExistingOrder(order) {
      if (!order) return;
      const shiftKey = this.availableShiftKeys[order.shift - 1];
      if (!shiftKey) return;

      this.activeShiftKey = shiftKey;
      this.selectedShiftIndex = this.availableShiftKeys.indexOf(shiftKey);

      order.transaction.forEach((product) => {
        const bucket = this.findSelectionBucketByCategoryAndShift(
          product.food.category.name,
          shiftKey
        );
        if (bucket) {
          const food = this.findMenuFood(bucket.shift, bucket.category, product.food_id);
          if (!food) return;

          food.quantity = product?.quantity || 0;
          product.food.quantity = product.quantity || 0;

          bucket.foods.push(product.food);

          this.hasExistingOrder = true;
        }
      });
    },
  },
  computed: {
    isCompanyUser() {
      return this.user?.type === "company";
    },
    user() {
      return this.$store.state.user.user;
    },
    selectedMenu() {
      return this.menus.find((i) => i.date === this.selectedDate);
    },
    availableShiftKeys() {
      if (!this.selectedMenu) return [];

      const preferredOrder = ["shiftOne", "shiftTwo", "shiftThree", "shiftFour"];
      const preferred = preferredOrder.filter((key) => this.selectedMenu[key]);
      const dynamic = Object.keys(this.selectedMenu).filter(
        (key) =>
          key.startsWith("shift") &&
          !preferredOrder.includes(key) &&
          this.selectedMenu[key]
      );

      return [...preferred, ...dynamic];
    },
    selectedShiftKey() {
      return this.availableShiftKeys[this.selectedShiftIndex] || null;
    },
    checkDisabled() {
      if (!this.restaurantOrderTime) return false;

      let day = moment().format("dddd").toLowerCase();
      let newDay = this.restaurantOrderTime[day];

      let addDay = newDay.day_after_limit;
      if (
        moment().isBefore(
          moment(this.selectedDate)
            .set("hour", newDay.time.time_hour)
            .set("minutes", newDay.time.time_minut)
        )
      ) {
        addDay = newDay.day_before_limit;
      }

      return (
        moment().add("days", addDay).diff(moment(this.selectedDate).add("days", 1)) >= 0
      );
    },
  },
  watch: {
    selectedShiftKey() {
      if (!this.selectedMenu || !this.selectedShiftKey) {
        this.selectedCategory = null;
        return;
      }

      this.selectedCategory =
        Object.keys(this.selectedMenu[this.selectedShiftKey] || {})[0] || null;
    },
    selectedMenu(menu) {
      if (menu) {
        if (
          this.selectedShiftIndex === null ||
          !this.availableShiftKeys[this.selectedShiftIndex]
        ) {
          this.selectedShiftIndex = this.availableShiftKeys.length ? 0 : null;
        }

        this.initializeSelectionBuckets(menu);
      }
    },
  },
};
</script>
